import { z } from 'zod';
import { blockTagId, newId } from '../ids.js';
import { blockRowSchema, instantSchema, taxonomyRowSchema, uuidSchema } from '../schema/rows.js';
import { sortKeyAfter } from '../sort-keys.js';
import { capturedTagInput, tagWrites } from './capture.js';
import { DomainError } from './types.js';
/**
 * Commands of the detail panel (f1-detail-panel, docs/04 §3 and §5): dates, note and tags of a
 * block. Each sends a PATCH with only the columns that change, or the join rows that change, so two
 * devices editing different fields of the same block converge (S1).
 */
const updateBlockDatesInput = z.object({
    block: z.object({
        id: uuidSchema,
        due_on: blockRowSchema.shape.due_on,
        start_on: blockRowSchema.shape.start_on,
    }),
    patch: z.object({
        due_on: blockRowSchema.shape.due_on.optional(),
        start_on: blockRowSchema.shape.start_on.optional(),
    }),
});
/** «Vence» and «Visible desde»; a null clears the date. */
export function updateBlockDates(input) {
    const { block, patch } = updateBlockDatesInput.parse(input);
    const data = {};
    if (patch.due_on !== undefined && patch.due_on !== block.due_on)
        data.due_on = patch.due_on;
    if (patch.start_on !== undefined && patch.start_on !== block.start_on) {
        data.start_on = patch.start_on;
    }
    if (Object.keys(data).length === 0)
        return [];
    return [{ table: 'blocks', op: 'PATCH', id: block.id, data }];
}
const updateBlockBodyInput = z.object({
    block: z.object({ id: uuidSchema, body: blockRowSchema.shape.body }),
    /** Markdown of the note (docs/08 §5); empty or blank clears it. */
    body: z.string().nullable(),
});
/** The note of the block, as Markdown. An empty note is stored as `null`. */
export function updateBlockBody(input) {
    const { block, body } = updateBlockBodyInput.parse(input);
    const next = body === null || body.trim() === '' ? null : body;
    if (next === block.body)
        return [];
    return [{ table: 'blocks', op: 'PATCH', id: block.id, data: { body: next } }];
}
const setBlockTagsInput = z.object({
    block: z.object({ id: uuidSchema, workspace_id: uuidSchema }),
    taxonomy: taxonomyRowSchema.pick({ id: true, workspace_id: true, cardinality: true }),
    /** Tags of this taxonomy the block already has. */
    current: z.array(z.object({ tag_id: uuidSchema })).default([]),
    /** Tags the block must end with: an existing one, or a name to create. */
    tags: z.array(capturedTagInput.shape.path.element).default([]),
    now: instantSchema.optional(),
});
/**
 * Tags of one taxonomy of a block, as the panel leaves them: creates the new ones with the same
 * deterministic ids as the capture, joins what is missing and deletes what is gone. The taxonomy
 * must belong to the block's workspace (INV-11); in a `one` taxonomy only the last tag stays
 * (INV-05).
 */
export function setBlockTags(input) {
    const { block, taxonomy, current, tags, now } = setBlockTagsInput.parse(input);
    if (taxonomy.workspace_id !== block.workspace_id) {
        throw new DomainError('invariant_violation', 'La taxonomía es de otro espacio.', 'INV-11');
    }
    const at = now ?? new Date().toISOString();
    const written = tagWrites(block.workspace_id, tags.map((segment) => ({ taxonomy, path: [segment] })), at);
    const wanted = new Map(written.leaves.map((leaf) => [leaf.tagId, leaf]));
    const had = new Set(current.map((row) => row.tag_id));
    const joins = [];
    for (const [tagId, leaf] of wanted) {
        if (had.has(tagId))
            continue;
        joins.push({
            table: 'block_tags',
            op: 'PUT',
            id: blockTagId(block.id, tagId),
            data: {
                workspace_id: block.workspace_id,
                block_id: block.id,
                tag_id: tagId,
                taxonomy_id: leaf.taxonomyId,
                created_at: at,
            },
        });
    }
    const removals = [...had]
        .filter((tagId) => !wanted.has(tagId))
        .map((tagId) => ({
        table: 'block_tags',
        op: 'DELETE',
        id: blockTagId(block.id, tagId),
    }));
    if (joins.length === 0 && removals.length === 0)
        return [];
    return [...written.tags, ...joins, ...removals];
}
const createSubtaskInput = z.object({
    id: uuidSchema.optional(),
    parent: z.object({ id: uuidSchema, workspace_id: uuidSchema }),
    created_by: uuidSchema,
    title: z.string().trim().min(1, 'El título no puede estar vacío').max(2000),
    /** Sort key of the last sibling, or null when the task has none yet. */
    last_sibling_sort_key: z.string().min(1).nullable().default(null),
    now: instantSchema.optional(),
});
/**
 * Creates a subtask under a task (EJE-09): same workspace as its parent (INV-02), open, with no
 * stage of its own, and after its siblings.
 */
export function createSubtask(input) {
    const parsed = createSubtaskInput.parse(input);
    const now = parsed.now ?? new Date().toISOString();
    return [
        {
            table: 'blocks',
            op: 'PUT',
            id: (parsed.id ?? newId()),
            data: {
                workspace_id: parsed.parent.workspace_id,
                parent_id: parsed.parent.id,
                sort_key: sortKeyAfter(parsed.last_sibling_sort_key),
                kind: 'task',
                title: parsed.title,
                body: null,
                stage_id: null,
                stage_changed_at: null,
                status: 'open',
                due_on: null,
                start_on: null,
                completed_at: null,
                created_by: parsed.created_by,
                props: {},
                created_at: now,
            },
        },
    ];
}
//# sourceMappingURL=detail.js.map